import React from 'react';
import { useNavigate } from 'react-router-dom';
import AnalyticsReports from '../components/AnalyticsReports';
import { Btn, Card, Note, PageBand, SectionHead, T } from '../components/ui';
import { useEmailManager } from '../index';

/**
 * `/admin/email/reports` — the board's third frame: what happened to email already sent.
 *
 * One section, not the two this described. The compliance footer moved to Settings, where the one
 * field has one editor, so the pairing this docblock explained no longer exists — and the sentence
 * was left pointing at a section that is not on the page.
 *
 * What remains is a campaign's aggregate outcome and the per-recipient lookup beneath it, which is
 * the pair that actually belongs together: the rates say whether a send went well, and the lookup
 * answers the ticket about the one fan for whom it did not.
 */
const Reports = () => {
  const navigate = useNavigate();
  const {
    campaigns,
    reportId,
    reportStats,
    reportLoading,
    loadReport,
    lookup,
    setLookup,
    findSends,
    sends,
    searchedFor,
    failed,
    isRunning,
    exportCsv,
    exporting,
    exportError,
    busy
  } = useEmailManager();

  return (
    <>
      <PageBand
        brand
        title="Reports"
        subtitle="How a campaign performed, and where any single message ended up"
        actions={
          <>
            <Btn variant="outline" onClick={() => navigate('/admin/email')}>
              Back to dashboard
            </Btn>
            <Btn variant="outline" icon="Gear" onClick={() => navigate('/admin/email/settings')}>
              Settings
            </Btn>
            {/*
              The same label and the same in-progress text as the Audience page's button, because
              it is the same export calling the same endpoint. It read "Export Audience Data" here
              and "Export Filtered List (CSV)" there, and gave no sign it had started — so an
              operator on a large audience pressed a dead-looking button twice.
            */}
            <Btn variant="outline" icon="Download" onClick={exportCsv} disabled={exporting}>
              {exporting ? 'Preparing…' : 'Export Filtered List (CSV)'}
            </Btn>
          </>
        }
      />

      {/*
        Anchored under the band that holds the button, rather than left as a toast or as nothing.

        A refused export was silent: the button un-dimmed and either a file appeared or it did not.
        This is the one place an operator is looking when they press it.
      */}
      {exportError ? <Note style={{ color: T.red }}>{exportError}</Note> : null}

      <Card id="analytics">
        <SectionHead icon="Chart" title="Email Analytics & Deliverability Reports" />
        <AnalyticsReports
          campaigns={campaigns}
          selectedId={reportId}
          onSelect={loadReport}
          onRefresh={() => loadReport(reportId)}
          stats={reportStats}
          loading={reportLoading}
          lookup={lookup}
          onLookupChange={setLookup}
          onLookup={findSends}
          sends={sends}
          searchedFor={searchedFor}
          lookupFailed={failed('sends')}
          searching={isRunning('find-sends')}
          busy={busy}
        />
      </Card>

      <Note>
        The board&apos;s Export All Data exports the filtered audience — the only export available,
        and the same one the Audience page offers. Campaigns, sends and settings cannot yet be
        exported together.
      </Note>
    </>
  );
};

export default Reports;
